/* sb-dc-quiet.js — take the design opinions out of the preflight. 2026-09-05.

   The proof panel listed thirteen findings and the bottom bar counted them all
   day ("Review advised 5"). Two of them meant the order could not be made; the
   rest were opinions about the artwork, wearing the same magenta and amber
   pills as the real ones. Customers are not print people: a badge that says
   Blocked reads as "this is broken" whether it is or not, and the ones that
   really mattered were lost in the pile.

   So the rule now is: a message appears when the MACHINE failed — the print
   file would not build, the store would not answer, the cart refused the line.
   Whether the artwork is a good idea is not the editor's call to make in red.

   How this can be done from a 3 KB file: the app bundle counts the findings
   immediately AFTER handing them to __dcClipIssues —

       window.__dcClipIssues && window.__dcClipIssues(i, r);
       let f = r.filter(e => e.severity === 'block').length,
           p = r.filter(e => e.severity === 'warn').length;

   — and `r` by then holds the bundle's own findings as well as the ones
   sb-dc-aug.js and sb-dc-nosafe.js pushed. Splicing entries out of that same
   array (in place — the bundle keeps the reference) removes them from the
   count, the bottom bar, the proof list and canApprove in one go. Neither the
   647 KB bundle nor the 322 KB augment file has to be rebuilt.

   Loads AFTER sb-dc-nosafe.js: nosafe rewrites wording on the way through, and
   this has to be the last link so it sees everything the earlier ones added.

   What is left, and why:
     empty           nothing on the canvas — the customer would pay for a blank
     safe-collapsed  finishing eats the whole print area, so there is no print
     needs-back      double-sided with an empty back (already handled upstream;
                     kept here so this file is not the thing that turned it off)

   ── the part that is NOT about the customer's screen ──────────────────────
   Taking a finding out of the array also drops `warnings` to 0, and the bundle
   computes

       autoRelease = blocking === 0 && warnings === 0 && layers.length > 0

   which is the flag that tells the print shop an order may skip review and go
   straight to the production queue. It is written into the order manifest
   (sb-dc-aug.js:5509) and shown on the bottom bar as "Can go straight to
   production".

   Measured on the live store 2026-09-05: loading the stock templates
   oval-soccer-6x4, bumper-sports-10x3 and "Events 01 Your Event" — no customer
   edits at all — flipped autoRelease from false to true once this file removed
   their warnings. A 20x20px photo on a 3in sticker (about 7 DPI) did the same.

   Quieting the SCREEN must not quiet the SHOP. sb-dc-override.js says the same
   thing about its own checkbox: "An acknowledged order still never skips review
   and goes straight to the production queue - the bundle keeps that strict on
   purpose." So __dcAutoOk is wrapped below: if this file hid anything, the
   order is not eligible to skip review, whatever the counts now say.

   window.__dcQuiet.last is what the most recent pass removed.

   Matching is on the issue id, not the wording, so a copy change upstream
   cannot silently switch a rule off. A NEW id upstream would not be matched
   and would show up on screen — the safe direction to fail in. */
(function () {
  'use strict';

  /* Checked first: `safe-collapsed` would otherwise be caught by the `safe-`
     prefix that removes the per-layer safe-area warnings. */
  var KEEP = { 'empty': 1, 'safe-collapsed': 1, 'needs-back': 1 };

  var DROP_PREFIX = [
    'safe-',        // "X is outside the safe area" — the safe area is off (sb-dc-nosafe.js)
    'curve-',       // "X curves outside the safe area" — same
    'grommet-',     // "grommets punched through it" — spacing is set on the shop floor
    'dpi-',         // resolution: block AND warn share this id. Both go.
    'legible-',     // text size: block AND warn share this id. Both go.
    'empty-text-',  // an empty text layer simply does not print
    'contrast-',    // 4.5:1 is a web accessibility threshold, not a print one
    'clipped-',     // text past the box edge — may well be deliberate
    'not-vector-'   // internal QA; there is no action the customer can take
  ];

  var DROP_EXACT = {
    'pocket-note': 1,       // the pole pocket is an option the customer chose
    'kiss-cut-path': 1,     // a sales note about the cut path, not a fault
    /* The size panel already reads "Producible: Combination not sold" live as
       the size is typed, so the customer knows at the moment they choose it
       rather than after the whole design is finished. */
    'size-unavailable': 1
  };

  function drops(id) {
    if (typeof id !== 'string' || KEEP[id]) return false;
    if (DROP_EXACT[id]) return true;
    for (var i = 0; i < DROP_PREFIX.length; i++) {
      if (id.indexOf(DROP_PREFIX[i]) === 0) return true;
    }
    return false;
  }

  /* Two slots, because a double-sided product runs the check once per side and
     we must not let a clean back erase a dirty front. Anything hidden in either
     of the last two passes means a person still looks at this order. */
  var recent = [0, 0];

  var prev = window.__dcClipIssues;
  window.__dcClipIssues = function (layers, issues) {
    if (typeof prev === 'function') { try { prev(layers, issues); } catch (e) {} }
    try {
      if (!Array.isArray(issues)) return;
      var gone = [];
      for (var i = issues.length - 1; i >= 0; i--) {          // 뒤에서부터 — 인덱스가 밀리지 않는다
        var it = issues[i];
        if (it && drops(it.id)) gone.push(issues.splice(i, 1)[0].id);
      }
      window.__dcQuiet.last = gone.reverse();
      recent.shift(); recent.push(gone.length);
    } catch (e) { /* a filter must never stop the editor */ }
  };

  /* 화면에서 뺐다고 공장 검수까지 빼지는 않는다 — 위 머리말 참고 */
  var prevAutoOk = window.__dcAutoOk;
  window.__dcAutoOk = function () {
    if (recent[0] || recent[1]) return false;
    return typeof prevAutoOk === 'function' ? prevAutoOk() : true;
  };

  /* Self-check. The prefix rules are blunt on purpose, and the one that can
     actually bite is `safe-` swallowing `safe-collapsed` — that would silently
     let a banner with no printable area through. Runs once at load; if it ever
     fails the console says so and the rules are wrong, not the page. */
  function test() {
    var bad = [];
    [['empty', false], ['safe-collapsed', false], ['needs-back', false],
     ['safe-abc', true], ['curve-abc', true], ['grommet-abc', true],
     ['dpi-abc', true], ['legible-abc', true], ['empty-text-abc', true],
     ['contrast-abc', true], ['clipped-abc', true], ['not-vector-abc', true],
     ['pocket-note', true], ['kiss-cut-path', true], ['size-unavailable', true],
     ['something-new', false], [null, false]
    ].forEach(function (c) { if (drops(c[0]) !== c[1]) bad.push(c[0]); });
    return bad;
  }

  window.__dcQuiet = { last: [], drops: drops, test: test,
    autoBlocked: function () { return !!(recent[0] || recent[1]); } };

  var failed = test();
  if (failed.length && window.console) {
    console.error('[sb-dc-quiet] rule self-check failed:', failed);
  }
})();
